import React, { useId } from "react"

import { Button } from "@coval/ui/button"
import { Plus } from "lucide-react"

import type {
  FilterConfig,
  TranscriptFilterConfig,
  TranscriptFilterJoin,
} from "@/types/metrics/metadata"

import {
  DraggableFilterList,
  JoinToggle,
  useFilterListSection,
} from "../../filter-list"
import { LABEL_STYLES } from "../../styles"
import { createDefaultRangeFilter, MAX_FILTERS_PER_CATEGORY } from "../types"
import { FilterCard } from "./FilterCard"
import { TranscriptPreview } from "./TranscriptPreview"

interface FilterListProps {
  config: TranscriptFilterConfig
  onChange: (config: TranscriptFilterConfig) => void
}

export const FilterList: React.FC<FilterListProps> = ({ config, onChange }) => {
  const includeFilters = config.include ?? []
  const excludeFilters = config.exclude ?? []
  const includeJoins = config.include_joins ?? []
  const excludeJoins = config.exclude_joins ?? []
  const baseId = useId()
  const includeLabelId = `${baseId}-include`
  const excludeLabelId = `${baseId}-exclude`

  const includeSection = useFilterListSection<FilterConfig>({
    filters: includeFilters,
    joins: includeJoins,
    max: MAX_FILTERS_PER_CATEGORY,
    createDefault: createDefaultRangeFilter,
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        include: nextFilters,
        include_joins: nextJoins as TranscriptFilterJoin[] | undefined,
      }),
  })

  const excludeSection = useFilterListSection<FilterConfig>({
    filters: excludeFilters,
    joins: excludeJoins,
    max: MAX_FILTERS_PER_CATEGORY,
    createDefault: createDefaultRangeFilter,
    onChange: (nextFilters, nextJoins) =>
      onChange({
        ...config,
        exclude: nextFilters,
        exclude_joins: nextJoins as TranscriptFilterJoin[] | undefined,
      }),
  })

  return (
    <div className="space-y-4">
      <div role="group" aria-labelledby={includeLabelId} className="space-y-2">
        <div className="flex items-center justify-between">
          <span id={includeLabelId} className={LABEL_STYLES}>
            Include
          </span>
        </div>
        {includeFilters.length === 0 ? (
          <Button
            variant="ghost"
            onClick={includeSection.handleAdd}
            aria-label="Add include filter"
            className="text-coval-text-muted"
          >
            <Plus />
            Add include filter
          </Button>
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<FilterConfig>
              filters={includeFilters}
              onChange={includeSection.handleUpdate}
              onRemove={includeSection.handleRemove}
              onReorder={includeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <FilterCard
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                />
              )}
              renderConnector={(index) => (
                <JoinToggle
                  value={includeJoins[index] ?? "or"}
                  onChange={(next) =>
                    includeSection.handleJoinChange(index, next)
                  }
                />
              )}
              getKey={(filter, index) => filter.id ?? `include-${index}`}
              ariaPrefix="include"
            />
            {includeSection.belowCap && (
              <Button
                variant="ghost"
                onClick={includeSection.handleAdd}
                aria-label="Add another include filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>

      <div role="group" aria-labelledby={excludeLabelId} className="space-y-2">
        <div className="flex items-center justify-between">
          <span id={excludeLabelId} className={LABEL_STYLES}>
            Exclude
          </span>
        </div>
        {excludeFilters.length === 0 ? (
          <Button
            variant="ghost"
            onClick={excludeSection.handleAdd}
            aria-label="Add exclude filter"
            className="text-coval-text-muted"
          >
            <Plus />
            Add exclude filter
          </Button>
        ) : (
          <div className="space-y-1">
            <DraggableFilterList<FilterConfig>
              filters={excludeFilters}
              onChange={excludeSection.handleUpdate}
              onRemove={excludeSection.handleRemove}
              onReorder={excludeSection.handleReorder}
              renderRow={({ filter, onChange: rowOnChange, onRemove }) => (
                <FilterCard
                  filter={filter}
                  onChange={rowOnChange}
                  onRemove={onRemove}
                />
              )}
              renderConnector={(index) => (
                <JoinToggle
                  value={excludeJoins[index] ?? "or"}
                  onChange={(next) =>
                    excludeSection.handleJoinChange(index, next)
                  }
                />
              )}
              getKey={(filter, index) => filter.id ?? `exclude-${index}`}
              ariaPrefix="exclude"
            />
            {excludeSection.belowCap && (
              <Button
                variant="ghost"
                onClick={excludeSection.handleAdd}
                aria-label="Add another exclude filter"
                className="text-coval-text-muted h-7 text-xs mt-2"
              >
                <Plus />
                Add another
              </Button>
            )}
          </div>
        )}
      </div>

      <TranscriptPreview config={config} />
    </div>
  )
}
